import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, XCircle, Send, LayoutTemplate, PenTool, Paperclip, FileText, X } from 'lucide-react';
import { supabase } from '../../services/supabase';
// Adjust these imports based on where you place the helper components
import RecipientInput from '../inputs/RecipientInput'; 
import RichTextEditor from '../editors/RichTextEditor';

const EmailComposeModal = ({ recipients: initialRecipients = [], onClose, onSend, contextClients = [] }) => {
    const [to, setTo] = useState(initialRecipients);
    const [cc, setCc] = useState([]);
    const [bcc, setBcc] = useState([]);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('<p><br/></p>');
    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [showCcBcc, setShowCcBcc] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [signatures, setSignatures] = useState([]);
    const [fileRules, setFileRules] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [selectedSigId, setSelectedSigId] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            const { data: tmplData, error: tmplError } = await supabase.from('email_templates').select('*');
            if (tmplError) console.error('Template load error:', tmplError);
            if (tmplData) setTemplates(tmplData);

            const { data: sigData, error: sigError } = await supabase.from('admin_signatures').select('*');
            if (sigError) console.error('Signature load error:', sigError);
            if (sigData) setSignatures(sigData);

            const { data: ruleData, error: ruleError } = await supabase.from('file_rules').select('*');
            if (ruleError) console.error('File rules load error:', ruleError);
            if (ruleData) setFileRules(ruleData);
        };
        fetchData();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString + 'T00:00:00');
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const applyTemplateContext = (text) => {
        if (!text) return '';
        const client = contextClients && contextClients.length > 0 ? contextClients[0] : {};
        
        // Debug: log the client data to verify it's available
        console.log('Template context client:', client);
        console.log('Original template text:', text);
        
        let result = text;
        // Handle percent-sign based placeholders: %COMPANYNAME%, %PREFERREDNAME%, etc.
        result = result
            .replace(/%COMPANYNAME%/gi, client.name || '')
            .replace(/%COMPANY_NAME%/gi, client.name || '')
            .replace(/%COMPANYNUMBER%/gi, client.number || '')
            .replace(/%COMPANY_NUMBER%/gi, client.number || '')
            .replace(/%CONTACTNAME%/gi, client.pc_fname || '')
            .replace(/%CONTACT_NAME%/gi, client.pc_fname || '')
            .replace(/%PREFERREDNAME%/gi, client.pc_pname || '')
            .replace(/%PREFERRED_NAME%/gi, client.pc_pname || '')
            .replace(/%EMAIL%/gi, client.pc_email || '')
            .replace(/%PHONE%/gi, client.pc_tel || '')
            .replace(/%TEL%/gi, client.pc_tel || '')
            .replace(/%NOTES%/gi, client.notes || '')
            .replace(/%NEXTACC%/gi, formatDate(client.nextAccMadeUpTo))
            .replace(/%NEXT_ACCOUNTS_MADE_UP_TO%/gi, formatDate(client.nextAccMadeUpTo))
            .replace(/%NEXTACCOUNTSDATE%/gi, formatDate(client.nextAccMadeUpTo))
            .replace(/%NEXTFILINGDATE%/gi, formatDate(client.nextFilingDate))
            .replace(/%NEXT_FILING_DATE%/gi, formatDate(client.nextFilingDate))
            // Also handle double-curly braces in case some templates use that format
            .replace(/\{\{\s*company_name\s*\}\}/gi, client.name || '')
            .replace(/\{\{\s*company_number\s*\}\}/gi, client.number || '')
            .replace(/\{\{\s*contact_name\s*\}\}/gi, client.pc_fname || '')
            .replace(/\{\{\s*preferred_name\s*\}\}/gi, client.pc_pname || '')
            .replace(/\{\{\s*email\s*\}\}/gi, client.pc_email || '')
            .replace(/\{\{\s*phone\s*\}\}/gi, client.pc_tel || '')
            .replace(/\{\{\s*notes\s*\}\}/gi, client.notes || '');
        
        console.log('Template after context applied:', result);
        return result;
    };

    const handleTemplateChange = (e) => {
        const tmplId = e.target.value;
        setSelectedTemplateId(tmplId);
        if (!tmplId) return;

        const template = templates.find(t => String(t.id) === tmplId);
        if (!template) {
            console.error('Template not found:', tmplId);
            return;
        }

        console.log('Selected template:', template);
        console.log('Context clients available:', contextClients);

        const newSubject = applyTemplateContext(template.subject || '');
        const newBody = applyTemplateContext(template.body_html || '');

        if (body !== '<p><br/></p>' && !confirm('This will replace your current message content. Continue?')) {
            setSelectedTemplateId('');
            return;
        }

        console.log('Setting subject to:', newSubject);
        console.log('Setting body to:', newBody);
        
        setSubject(newSubject);
        setBody(newBody);
    };

    const handleSignatureChange = (e) => {
        const sigId = e.target.value;
        setSelectedSigId(sigId);
        if (!sigId) return;

        const sig = signatures.find(s => String(s.id) === sigId);
        if (sig) {
            setBody(prev => prev + '<br/><br/>' + sig.html_content);
            setTimeout(() => setSelectedSigId(''), 300);
        }
    };

    const validateFile = (file) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (!ext) {
            return { valid: false, message: 'File type not recognized.' };
        }

        const rule = fileRules.find(r => String(r.extension || '').toLowerCase() === ext);
        if (!rule) {
            return { valid: false, message: `.${ext} files are not permitted.` };
        }

        const status = String(rule.status || '').toLowerCase();
        if (status === 'blocked') {
            return { valid: false, message: rule.message || `.${ext} files are blocked for security reasons.` };
        }
        if (status === 'legacy') {
            return { valid: false, message: rule.message || `.${ext} is a legacy format. Please use the modern format.` };
        }

        return { valid: true };
    };

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length === 0) return;

        const validFiles = [];
        selected.forEach(file => {
            const { valid, message } = validateFile(file);
            if (!valid) {
                alert(message);
            } else {
                validFiles.push(file);
            }
        });

        if (validFiles.length > 0) {
            setFiles(prev => [...prev, ...validFiles]);
        }

        e.target.value = '';
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = Array.from(e.dataTransfer.files || []);
        if (dropped.length === 0) return;

        const validFiles = [];
        dropped.forEach(file => {
            const { valid, message } = validateFile(file);
            if (!valid) {
                alert(message);
            } else {
                validFiles.push(file);
            }
        });

        if (validFiles.length > 0) {
            setFiles(prev => [...prev, ...validFiles]);
        }
    };

    const convertFileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result || '';
            const base64 = String(result).split(',')[1] || '';
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const handleSend = async () => {
        if (to.length === 0 && cc.length === 0 && bcc.length === 0) {
            alert("Please add at least one recipient.");
            return;
        }
        if (!subject) {
            alert("Please add a subject.");
            return;
        }
        setIsSending(true);
        try {
            const attachments = await Promise.all(
                files.map(async (file) => ({
                    filename: file.name,
                    content: await convertFileToBase64(file)
                }))
            );

            await onSend({ to, cc, bcc, subject, html: body, attachments });
            setIsSending(false);
            onClose();
        } catch (err) {
            console.error('Send error:', err);
            setIsSending(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl border border-slate-200 overflow-hidden flex flex-col h-[90vh] animate-in zoom-in-95 duration-200">
                <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-gradient-to-r from-blue-600 to-indigo-600">
                    <div>
                        <h3 className="text-lg font-bold text-white flex items-center gap-3">
                            <Mail className="w-6 h-6" />
                            Compose Email
                        </h3>
                        <p className="text-xs text-blue-100 mt-0.5">Craft and send professional emails with templates and signatures</p>
                    </div>
                    <button onClick={onClose} className="text-blue-100 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"><XCircle className="w-6 h-6" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-gradient-to-b from-slate-50 to-white">
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-5">
                        <div className="flex justify-end">
                            <button 
                                onClick={() => setShowCcBcc(!showCcBcc)} 
                                className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-md transition-colors uppercase tracking-wide"
                            >
                                {showCcBcc ? '− Hide Cc/Bcc' : '+ Show Cc/Bcc'}
                            </button>
                        </div>

                        <RecipientInput 
                            label="To" 
                            recipients={to} 
                            onAdd={(r) => setTo([...to, r])} 
                            onRemove={(i) => setTo(to.filter((_, idx) => idx !== i))} 
                        />

                        {showCcBcc && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 fade-in duration-300">
                                <RecipientInput 
                                    label="Cc" 
                                    recipients={cc} 
                                    onAdd={(r) => setCc([...cc, r])} 
                                    onRemove={(i) => setCc(cc.filter((_, idx) => idx !== i))} 
                                />
                                <RecipientInput 
                                    label="Bcc" 
                                    recipients={bcc} 
                                    onAdd={(r) => setBcc([...bcc, r])} 
                                    onRemove={(i) => setBcc(bcc.filter((_, idx) => idx !== i))} 
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Subject Line</label>
                            <input 
                                type="text" 
                                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-slate-900 placeholder-slate-400 transition-all" 
                                placeholder="Enter email subject..." 
                                value={subject} 
                                onChange={e => setSubject(e.target.value)} 
                            />
                        </div>
                    </div>

                    <div className="space-y-3 flex-1 flex flex-col min-h-[350px]">
                        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-200">
                            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Message Body</label>

                            <div className="flex items-center gap-5">
                                <div className="flex items-center gap-2.5">
                                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><LayoutTemplate className="w-4 h-4 text-blue-600"/> Template:</span>
                                    <select 
                                        className="text-xs border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-sm hover:border-blue-400 transition-all"
                                        onChange={handleTemplateChange}
                                        value={selectedTemplateId}
                                    >
                                        <option value="">-- Select a template --</option>
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center gap-2.5">
                                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><PenTool className="w-4 h-4 text-blue-600"/> Signature:</span>
                                    <select 
                                        className="text-xs border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-sm hover:border-blue-400 transition-all"
                                        onChange={handleSignatureChange}
                                        value={selectedSigId}
                                    >
                                        <option value="">-- Select signature --</option>
                                        {signatures.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex-1 flex flex-col shadow-sm">
                            <RichTextEditor value={body} onChange={setBody} />
                        </div>
                    </div>

                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                                <Paperclip className="w-4 h-4 text-blue-600" /> Attachments
                            </label>
                            <button 
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold px-3 py-1.5 rounded transition-colors"
                            >
                                <Paperclip className="w-3.5 h-3.5" /> Add Files
                            </button>
                            <input 
                                type="file" 
                                multiple 
                                ref={fileInputRef} 
                                className="hidden" 
                                onChange={handleFileChange} 
                            />
                        </div>

                        <div 
                            className={`${files.length > 0 ? 'min-h-[44px] max-h-[160px] overflow-y-auto' : 'h-20'} transition-all duration-200 rounded-lg ${
                                isDragging 
                                ? 'bg-blue-50 border-2 border-dashed border-blue-400 ring-4 ring-blue-50/50' 
                                : 'bg-slate-50/40 border-2 border-dashed border-slate-200'
                            }`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            {files.length > 0 ? (
                                <div className="flex flex-wrap gap-2 p-2">
                                    {files.map((file, index) => (
                                        <div key={index} className="flex items-center gap-2 bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg text-xs text-blue-800 shadow-sm animate-in zoom-in-95">
                                            <FileText className="w-4 h-4 flex-shrink-0 opacity-70" />
                                            <div className="flex flex-col">
                                                <span className="truncate max-w-[180px] font-medium">{file.name}</span>
                                                <span className="text-[10px] opacity-70">{(file.size / 1024).toFixed(1)} KB</span>
                                            </div>
                                            <button onClick={() => removeFile(index)} className="text-blue-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors ml-1"><X className="w-3.5 h-3.5" /></button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <button 
                                    type="button"
                                    className={`w-full text-xs italic py-2 text-center transition-colors ${
                                        isDragging ? 'text-blue-700' : 'text-slate-500'
                                    }`}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {isDragging ? 'Drop files to attach' : 'Drag & drop files here, or click to select'}
                                </button>
                            )}
                        </div>

                        {fileRules.length > 0 && (
                            <p className="text-[11px] text-slate-500">
                                Allowed file types are managed by security rules and updated regularly.
                            </p>
                        )}
                    </div>
                </div>
                <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-t border-slate-200 flex justify-end gap-3 shadow-sm">
                    <button 
                        onClick={onClose} 
                        className="px-6 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Discard
                    </button>
                    <button 
                        onClick={handleSend} 
                        disabled={isSending} 
                        className="flex items-center gap-2 px-8 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-md transition-all"
                    >
                        {isSending ? 'Sending...' : <><Send className="w-4 h-4" /> Send Email</>}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EmailComposeModal;